import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth, BACKEND_URL } from "./context/AuthContext";
import { PlayerProvider } from "./context/PlayerContext";
import { useState, useEffect } from "react";
import AuthCallback from "./components/AuthCallback";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import AdminPanel from "./pages/AdminPanel";
import AIStudio from "./pages/AIStudio";
import FeaturesPage from "./pages/FeaturesPage";
import PublicStationPage from "./pages/PublicStationPage";
import WhiteLabelPlayer from "./pages/WhiteLabelPlayer";
import TermsOfUse from "./pages/TermsOfUse";
import DMCAPolicy from "./pages/DMCAPolicy";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import SetupGuide from "./pages/SetupGuide";
import Shop from "./pages/Shop";
import Network from "./pages/Network";
import DiscoverPage from "./pages/DiscoverPage";
import WhyUs from "./pages/WhyUs";
import GoogleCallback from "./pages/GoogleCallback";
import NewsPage from "./pages/NewsPage";
import NewsPostPage from "./pages/NewsPostPage";
import SubmitPage from "./pages/SubmitPage";
import ArtistsPage from "./pages/ArtistsPage";
import AffiliatePage from "./pages/AffiliatePage";
import AboutPage from "./pages/AboutPage";
import LoginPage from "./pages/LoginPage";
import SubmissionTerms from "./pages/SubmissionTerms";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import NotFoundPage from "./pages/NotFoundPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ArtistProfilePage from "./pages/ArtistProfilePage";
import AdvertisePage from "./pages/AdvertisePage";
import MaintenancePage from "./pages/MaintenancePage";
import StationPage from "./pages/StationPage";
import DirectoryPage from "./pages/DirectoryPage";
import DirectorySubmitPage from "./pages/DirectorySubmitPage";
import GlobalPlayer from "./components/GlobalPlayer";
import ChatWidget from "./components/ChatWidget";

// Only show ChatWidget on main site pages, not on /station/ or /player/ or /directory routes
const MainSiteChatWidget = () => {
  const location = useLocation();
  const hide = location.pathname.startsWith("/station/") || location.pathname.startsWith("/player/") || location.pathname.startsWith("/directory");
  if (hide) return null;
  return <ChatWidget />;
};
import ScrollToTop from "./components/ScrollToTop";
import CookieConsent from "./components/CookieConsent";
import CallInPage from "./pages/CallInPage";
import { Toaster } from "sonner";
import "./App.css";

/* Vanity URL component — /:slug routes here as catch-all */
function VanityRoute() {
  const { vanitySlug } = useParams();
  return <WhiteLabelPlayer overrideSlug={vanitySlug} />;
}

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

function AppRouter() {
  const location = useLocation();
  const { user, loading: authLoading, setUserData } = useAuth();
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/maintenance/status`)
      .then(r => r.json())
      .then(data => { setMaintenance(data); setMaintenanceChecked(true); })
      .catch(() => setMaintenanceChecked(true));
  }, []);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isAdmin = user?.is_admin || user?.email === "wnuv98.5@gmail.com";

  if (maintenanceChecked && maintenance?.enabled && !isAdmin && !authLoading) {
    return (
      <MaintenancePage
        message={maintenance.message}
        estimatedReturn={maintenance.estimated_return}
        contactEmail={maintenance.contact_email}
        onAdminLogin={(userData, token) => setUserData(userData, token)}
      />
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/listen/:stationId" element={<PublicStationPage />} />
        <Route path="/player/:slug" element={<WhiteLabelPlayer />} />
        <Route path="/station/:slug" element={<StationPage />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/dmca" element={<DMCAPolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/guide" element={<SetupGuide />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/network" element={<Network />} />
        <Route path="/discover" element={<DiscoverPage />} />
        <Route path="/why-us" element={<WhyUs />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:slug" element={<NewsPostPage />} />
        <Route path="/submit" element={<SubmitPage />} />
        <Route path="/artists" element={<ArtistsPage />} />
        <Route path="/artist/stats" element={<ArtistsPage />} />
        <Route path="/affiliate" element={<AffiliatePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/submission-terms" element={<SubmissionTerms />} />
        <Route path="/auth/verify" element={<VerifyEmailPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/artist/:slug" element={<ArtistProfilePage />} />
        <Route path="/advertise" element={<AdvertisePage />} />
        <Route path="/directory" element={<DirectoryPage />} />
        <Route path="/directory/submit" element={<DirectorySubmitPage />} />
        <Route path="/callin" element={<CallInPage />} />
        <Route path="/ai-studio" element={<AIStudio />} />
        {/* Vanity URL catch-all — must be LAST */}
        <Route path="/:vanitySlug" element={<VanityRoute />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <GlobalPlayer />
      <MainSiteChatWidget />
      <ScrollToTop />
      <CookieConsent />
      <Toaster position="bottom-right" richColors theme="dark" />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
    <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID, currency: "USD" }}>
      <AuthProvider>
        <PlayerProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </div>
        </PlayerProvider>
      </AuthProvider>
    </PayPalScriptProvider>
    </HelmetProvider>
  );
}

export default App;
